import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { DrawerHeader } from '@components/drawer-header';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDevices, deleteDevice, addDevice } from '@service/device';
import {
  Box,
  Grid,
  Modal,
  IconButton,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Icon } from '@components/icon';
import { useState, useEffect, useCallback, useContext } from 'react';
import { TDeviceData, TDeviceApiKey } from '@models/device-data';
import * as S from './styles';

export function Devices() {
  const [devices, setDevices] = useState<TDeviceData[]>([]);
  const [deleteDeviceModalOpen, setDeleteDeviceModalOpen] =
    useState<boolean>(false);
  const [row, setRow] = useState<TDeviceData>();
  const [newDeviceModalOpen, setNewDeviceModalOpen] = useState<boolean>(false); // New state for the second modal
  const [newDeviceName, setNewDeviceName] = useState<string>(''); // State for new device name
  const [newDeviceApiKey, setNewDeviceApiKey] = useState<string>(''); // State for new device API key
  const [createDeviceError, setCreateDeviceError] = useState<string>(''); // State for new device API key
  const queryClient = useQueryClient();

  const devicesQuery = useQuery({
    queryKey: ['getDevices'],
    queryFn: getDevices,
  });


  useEffect(() => {
    setDevices(devicesQuery?.data?.data as TDeviceData[]);
  }, [devicesQuery.data]);


  const deleteMutation = useMutation<void, Error, TDeviceData>({
    mutationFn: (data) => deleteDevice({ device_name: data.device_name }),
  });

  
  const createNewMutation = useMutation<TDeviceApiKey, Error, TDeviceData>({
    mutationFn: (data) => addDevice({ device_name: data.device_name }),
  });

  const handleModalAndRowOpen = useCallback((selectedRow: TDeviceData) => {
    setDeleteDeviceModalOpen(true);
    setRow(selectedRow);
  }, []);

  const handleDeviceDelete = useCallback(async () => {
    try {
      await deleteMutation.mutateAsync({
        device_name: row?.device_name as string,
      });
      
      queryClient.invalidateQueries({
        queryKey: ['getDevices']
      });

      setDeleteDeviceModalOpen(false);
    } catch (err) {
      setDeleteDeviceModalOpen(false);
    }
  }, [queryClient, row?.device_name, deleteMutation]);

  const handleNewDeviceModalOpen = useCallback(() => {
    setNewDeviceModalOpen(true);
    setNewDeviceName('');
    setNewDeviceApiKey('');
    setCreateDeviceError('');
  }, []);

  const handleNewDeviceModalClose = useCallback(() => {
    setNewDeviceModalOpen(false);
    setNewDeviceName('');
    setNewDeviceApiKey('');
    setCreateDeviceError('');
  }, []);

  const handleNewDeviceSubmit = useCallback(async () => {
    if (!newDeviceName) {
      return;
    }

    try {
      const response = await createNewMutation.mutateAsync({
        device_name: newDeviceName as string,
      });

      queryClient.invalidateQueries({
        queryKey: ['getDevices']
      });


      const apiKey = response.api_key;
      setNewDeviceApiKey(apiKey);
      setCreateDeviceError('');
    } catch (err) {
      setCreateDeviceError(
        'Dispositivo com esse nome ja existe. Tente novamente.'
      );
    }
  }, [queryClient, createNewMutation]);

  const handleCopyApiKey = () => {
    // Copy the API key to clipboard
    navigator.clipboard.writeText(newDeviceApiKey);
  };

  return (
    <DrawerHeader title="Gerenciamento de dispositivos">
      <Box display="flex" justifyContent="flex-end">
        <S.ButtonNewDevice onClick={handleNewDeviceModalOpen}>
          Novo dispositivo
        </S.ButtonNewDevice>
      </Box>
      {devicesQuery.isLoading ? (
        <Grid container alignItems="center" justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }} aria-label="users table">
            <S.TableHead>
              <TableRow>
                <TableCell
                  style={{
                    borderBottom: '1px solid #a51033',
                    height: '70px',
                  }}
                />
                <S.TableCellHeader>Nome do dispositivo</S.TableCellHeader>
                <S.TableCellHeader></S.TableCellHeader>
              </TableRow>
            </S.TableHead>
            <TableBody>
              {devices?.map((element: TDeviceData) => (
                <TableRow
                  key={element.device_name}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                >
                  <TableCell component="th" scope="row" />
                  <S.TableCell>{element.device_name}</S.TableCell>
                  <S.TableCell>
                    <S.IconBox
                      onClick={() =>
                        handleModalAndRowOpen(element as TDeviceData)
                      }
                    >
                      <Icon name="trash" alt="trash icon" width={36} />
                    </S.IconBox>
                  </S.TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {deleteDeviceModalOpen && (
        <Modal
          open={deleteDeviceModalOpen}
          onClose={() => setDeleteDeviceModalOpen(false)}
        >
          <S.ModalDeleteContainer>
            <Box
              maxWidth={200}
              justifyContent="center"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                variant="h5"
                color="primary"
                fontWeight={600}
                textAlign="center"
                mt={2}
              >
                Tem certeza que deseja deletar este dispositivo?
              </Typography>
              <S.ButtonContainer>
                <S.ButtonYes onClick={() => handleDeviceDelete()}>
                  SIM
                </S.ButtonYes>
                <S.ButtonNo onClick={() => setDeleteDeviceModalOpen(false)}>
                  NÃO
                </S.ButtonNo>
              </S.ButtonContainer>
            </Box>
          </S.ModalDeleteContainer>
        </Modal>
      )}
      {newDeviceModalOpen && (
        <Modal open={newDeviceModalOpen} onClose={handleNewDeviceModalClose}>
          <S.ModalAddDeviceContainer>
            <IconButton
              aria-label="Fechar"
              color="inherit"
              onClick={handleNewDeviceModalClose}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            >
              <Close />
            </IconButton>
            <Box
              maxWidth={500}
              justifyContent="center"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              {!newDeviceApiKey && (
                <Box
                  justifyContent="center"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Typography
                    variant="h4"
                    color="primary"
                    fontWeight={600}
                    textAlign="center"
                    mt={2}
                  >
                    Digite abaixo o nome do novo dispositivo
                  </Typography>
                  <TextField
                    label="Digite aqui o nome do novo dispositivo"
                    value={newDeviceName}
                    onChange={(e) => setNewDeviceName(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <Typography
                    variant="h6"
                    color="primary"
                    fontWeight={100}
                    textAlign="center"
                    mt={2}
                  >
                    {createDeviceError}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNewDeviceSubmit}
                  >
                    Prosseguir
                  </Button>
                </Box>
              )}
              {newDeviceApiKey && (
                <Box
                  justifyContent="center"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Icon disableTheming name="aprove" alt="aprive icon" width={50} />
                  <Typography
                    variant="h4"
                    color="primary"
                    fontWeight={600}
                    textAlign="center"
                    mt={2}
                  >
                    Dispositivo criado com sucesso
                  </Typography>
                  <TableContainer component={Paper} elevation={0}>
                    <Table sx={{ minWidth: 450 }} aria-label="users table">
                      <S.TableHead>
                        <TableRow>
                          <TableCell
                            style={{
                              borderBottom: '1px solid #a51033',
                              height: '70px',
                            }}
                          />
                          <S.TableCellHeader>
                            Nome do dispositivo
                          </S.TableCellHeader>
                          <S.TableCellHeader>Chave</S.TableCellHeader>
                          <S.TableCellHeader></S.TableCellHeader>
                        </TableRow>
                      </S.TableHead>
                      <TableBody>
                        <TableRow
                          key={newDeviceApiKey}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row" />
                          <S.TableCell>{newDeviceName}</S.TableCell>
                          <S.TableCell>{newDeviceApiKey}</S.TableCell>
                          <S.TableCell>
                            <S.IconBox onClick={() => handleCopyApiKey()}>
                              <Icon name="copy" alt="copy icon" width={20} />
                            </S.IconBox>
                          </S.TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography
                    variant="h6"
                    color="primary"
                    fontWeight={100}
                    textAlign="center"
                    mt={2}
                  >
                    Copie a chave e guarde. Após fechar essa janela você não
                    terá mais acesso a ela.
                  </Typography>
                </Box>
              )}
            </Box>
          </S.ModalAddDeviceContainer>
        </Modal>
      )}
    </DrawerHeader>
  );
}
